import { useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { workspaceMembershipListForWorkspaceQueryDocument } from "./workspace-membership.gql";

export interface UseGetWorkspaceMembershipListProps {
  workspaceId: string;
}

export const useWorkspaceMembershipList = (props: UseGetWorkspaceMembershipListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.workspaces.membershipList(props.workspaceId),
    queryFn: async () => {
      const client = createOperationClient("workspaceMembershipListForWorkspace");
      const data = await client.request(workspaceMembershipListForWorkspaceQueryDocument, { input: props.workspaceId });
      return data.workspaceMembershipListForWorkspace;
    },
    enabled: !!props.workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes for individual items
  });

  const workspaceMembershipList = useMemo(() => {
    return queryResult.data ?? [];
  }, [queryResult.data]);

  const activeWorkspaceMembershipList = useMemo(() => {
    return workspaceMembershipList.filter((wsm) => wsm.status === "ACTIVE");
  }, [workspaceMembershipList]);

  const activeOrPendingWorkspaceMembershipList = useMemo(() => {
    return workspaceMembershipList.filter((wsm) => wsm.status === "ACTIVE" || wsm.status === "PENDING");
  }, [workspaceMembershipList]);

  return {
    queryResult,
    loading: queryResult.isLoading,
    error: queryResult.error,
    workspaceMembershipList,
    activeWorkspaceMembershipList,
    activeOrPendingWorkspaceMembershipList,
  };
};
